import { Caption, H4 } from '@liveauctioneers/hammer-ui-core/text';
import styled from 'styled-components';

import { RouteLink } from '@/components/RouteLinks/RouteLinks';
import { useAppSelector } from '@/redux/hooks';
import { featuredCategoriesSelector } from '@/redux/modules/featuredCategories';
import { FeaturedCategory } from '@/types/FeaturedCategory';
import { addImageParams } from '@/utils/urls';

const imageWidth = 165;
const imageHeight = 110;

const HeroBannerFeaturedCardList = () => {
    const categories = useAppSelector(featuredCategoriesSelector);
    const categoriesToShow = categories.slice(0, 4);

    const featuredCards = categoriesToShow.map(({ imageUrl, label, link, title }: FeaturedCategory, index: number) => {
        const src = addImageParams({
            height: imageHeight,
            url: imageUrl,
            width: imageWidth,
        });
        const highDpiSrc = addImageParams({
            height: imageHeight * 2,
            url: imageUrl,
            width: imageWidth * 2,
        });

        return (
            <StyledFeaturedListItem
                className="group"
                data-testid="categoryCard"
                key={`${title}-${index}`}
            >
                <StyledFeaturedCardWrapper>
                    <RouteLink
                        className="block h-full w-full no-underline"
                        to={link}
                    >
                        <StyledImageWrapper>
                            <StyledImage
                                alt={title}
                                height={imageHeight}
                                src={src}
                                srcSet={`${highDpiSrc} 2x`}
                                width={imageWidth}
                            />
                        </StyledImageWrapper>
                        <H4 data-testid="categoryTitle">{title}</H4>
                        <StyledCaption>{label}</StyledCaption>
                    </RouteLink>
                </StyledFeaturedCardWrapper>
            </StyledFeaturedListItem>
        );
    });

    return (
        <StyledFeaturedListContainer>
            <StyledFeaturedList>{featuredCards}</StyledFeaturedList>
        </StyledFeaturedListContainer>
    );
};

export default HeroBannerFeaturedCardList;

const StyledFeaturedListContainer = styled.div`
    overflow: hidden;
    display: flex;
    justify-content: space-around;
    width: 57%;

    @media (max-width: 930px) {
        width: 100%;
    }
`;

const StyledFeaturedList = styled.div`
    display: flex;
    align-items: baseline;

    margin-left: 20px;
    width: 100%;
    padding-top: 10px;

    @media (max-width: 930px) {
        margin-left: 0;
        padding-top: 0;
    }
`;

const StyledFeaturedListItem = styled.div`
    flex: 1 1 0px;
    text-align: center;
    margin-top: 22px;

    // hide the 4th card when below 1100px
    @media (max-width: 1100px) {
        &:nth-child(4) {
            display: none;
        }
    }
`;

const StyledCaption = styled(Caption)`
    && {
        color: ${({ theme }) => theme.colors.grey100};
        ${/* sc-selector */ StyledFeaturedListItem}:hover & {
            color: ${({ theme }) => theme.colors.blue200};
        }
    }
`;

const StyledFeaturedCardWrapper = styled.div`
    &:hover span {
        color: ${({ theme }) => theme.colors.blue200};
    }
`;

const StyledImageWrapper = styled.div`
    padding-bottom: 66.66666%;
    position: relative;
`;

const StyledImage = styled.img`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    left: 0;
    margin: auto;
    display: block;
    max-height: 100%;
    max-width: 100%;
    background-color: ${({ theme }) => theme.colors.white};
`;
